import React, { useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import {
  EmptyState,
  PortalLoading,
  StatusBadge,
  TeacherPageHeader,
  niceDate,
  niceDateTime,
  useTeacherPortal,
} from "./teacherPortalUtils";

const emptyForm = {
  title: "",
  category: "",
  class_subject: "",
  resource_type: "HOMEWORK",
  description: "",
  content: "",
  external_url: "",
  access_level: "ALL_STUDENTS",
  status: "PUBLISHED",
  publish_date: "",
  due_date: "",
  file: null,
};

function TeacherLearningPage() {
  const { data, loading, reload } = useTeacherPortal("teacher-portal/learning/");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [feedbackFiles, setFeedbackFiles] = useState({});
  const [reviewingId, setReviewingId] = useState(null);

  if (loading && !data) {
    return <PortalLoading label="Loading homework and resources..." />;
  }

  const allocations = data?.allocations || [];
  const categories = data?.categories || [];
  const resources = data?.resources || [];

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) return toast.warning("Enter a title.");
    if (!form.class_subject) return toast.warning("Select a class and subject.");
    if (["HOMEWORK", "ASSIGNMENT"].includes(form.resource_type) && !form.due_date) {
      return toast.warning("Select a due date.");
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "file") {
        if (value) payload.append("file", value);
        return;
      }
      if (value !== null && value !== undefined) payload.append(key, value);
    });

    try {
      setSaving(true);
      await axiosInstance.post("teacher-portal/learning/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        ["HOMEWORK", "ASSIGNMENT"].includes(form.resource_type)
          ? "Assigned work published successfully."
          : "Learning resource saved successfully."
      );
      setForm(emptyForm);
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save the learning resource.");
    } finally {
      setSaving(false);
    }
  };

  const loadSubmissions = async (item) => {
    try {
      setLoadingSubmissions(true);
      setSelectedAssignment(item);
      const response = await axiosInstance.get(
        `teacher-portal/assignments/${item.id}/submissions/`
      );
      setSubmissionData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load student submissions.");
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const review = async (submission, nextStatus) => {
    const payload = new FormData();
    payload.append("status", nextStatus);
    payload.append("teacher_feedback", feedback[submission.id] || "");
    const file = feedbackFiles[submission.id];
    if (file) payload.append("feedback_file", file);

    try {
      setReviewingId(submission.id);
      const response = await axiosInstance.patch(
        `teacher-portal/assignment-submissions/${submission.id}/review/`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(
        nextStatus === "RESUBMIT_REQUESTED"
          ? "Resubmission requested."
          : "Submission marked as reviewed."
      );
      if (selectedAssignment) await loadSubmissions(selectedAssignment);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save review.");
    } finally {
      setReviewingId(null);
    }
  };

  const assignedWork = resources.filter((item) =>
    ["HOMEWORK", "ASSIGNMENT"].includes(item.resource_type)
  );
  const otherResources = resources.filter(
    (item) => !["HOMEWORK", "ASSIGNMENT"].includes(item.resource_type)
  );

  return (
    <div className="container-fluid py-4">
      <TeacherPageHeader
        title="Homework, Assignments & Learning"
        subtitle="Publish work, receive student files, review submissions and request resubmission."
        icon="bi-journal-text"
        actions={<button className="btn btn-outline-success" onClick={reload}>Refresh</button>}
      />

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="fw-bold mb-1">Create Homework / Assignment / Resource</h5>
          <small className="text-muted">
            Homework and assignments allow students to return their completed work directly through the portal.
          </small>
        </div>

        <div className="card-body">
          <form className="row g-3" onSubmit={save}>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Type</label>
              <select className="form-select" value={form.resource_type}
                onChange={(e) => update("resource_type", e.target.value)}>
                <option value="HOMEWORK">Homework</option>
                <option value="ASSIGNMENT">Assignment</option>
                <option value="NOTE">Notes / Document</option>
                <option value="VIDEO">Video</option>
                <option value="LINK">External Link</option>
                <option value="PAST_PAPER">Past Paper</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Class & Subject</label>
              <select className="form-select" value={form.class_subject}
                onChange={(e) => update("class_subject", e.target.value)} required>
                <option value="">Select class and subject</option>
                {allocations.map((item) => (
                  <option key={item.id} value={item.class_subject}>
                    {item.class_group_name} - {item.subject_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-8">
              <label className="form-label fw-semibold">Title</label>
              <input className="form-control" value={form.title}
                onChange={(e) => update("title", e.target.value)}
                placeholder="e.g. Mathematics Homework - Fractions" required />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Category</label>
              <select className="form-select" value={form.category}
                onChange={(e) => update("category", e.target.value)}>
                <option value="">No category</option>
                {categories.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">Instructions / Description</label>
              <textarea className="form-control" rows="4" value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Give students clear instructions about the work to complete." />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">Additional Content</label>
              <textarea className="form-control" rows="3" value={form.content}
                onChange={(e) => update("content", e.target.value)}
                placeholder="Questions, reading instructions, exercises or other content." />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Publish Date</label>
              <input type="date" className="form-control" value={form.publish_date}
                onChange={(e) => update("publish_date", e.target.value)} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Due Date</label>
              <input type="date" className="form-control" value={form.due_date}
                onChange={(e) => update("due_date", e.target.value)} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Status</label>
              <select className="form-select" value={form.status}
                onChange={(e) => update("status", e.target.value)}>
                <option value="PUBLISHED">Published</option>
                <option value="DRAFT">Draft</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Teacher Attachment</label>
              <input type="file" className="form-control"
                onChange={(e) => update("file", e.target.files?.[0] || null)} />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">External Link</label>
              <input type="url" className="form-control" value={form.external_url}
                onChange={(e) => update("external_url", e.target.value)}
                placeholder="https://..." />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Student Access</label>
              <select className="form-select" value={form.access_level}
                onChange={(e) => update("access_level", e.target.value)}>
                <option value="ALL_STUDENTS">All Students</option>
                <option value="PAID_ONLY">Paid Students Only</option>
                <option value="PREVIEW">Preview Available</option>
              </select>
            </div>

            <div className="col-12">
              <button className="btn btn-success" disabled={saving}>
                {saving ? "Saving..." : "Publish / Save"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white">
          <h6 className="fw-bold mb-0">Assigned Work ({assignedWork.length})</h6>
        </div>
        <div className="card-body">
          {assignedWork.length === 0 ? (
            <EmptyState title="No homework or assignments published yet" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Work</th><th>Class</th><th>Subject</th><th>Type</th><th>Due</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {assignedWork.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.title}</strong>
                        <div className="small text-muted">{item.description || "No additional instructions"}</div>
                      </td>
                      <td>{item.class_group_name}</td>
                      <td>{item.subject_name}</td>
                      <td><StatusBadge value={item.resource_type} /></td>
                      <td>{niceDate(item.due_date)}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-success" onClick={() => loadSubmissions(item)}>
                          Student Submissions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedAssignment && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white d-flex flex-wrap justify-content-between gap-2">
            <div>
              <h5 className="fw-bold mb-0">Student Submissions</h5>
              <small className="text-muted">{selectedAssignment.title}</small>
            </div>
            {submissionData && (
              <div className="d-flex gap-2">
                <span className="badge text-bg-success">Submitted {submissionData.submitted_count}</span>
                <span className="badge text-bg-secondary">Not submitted {submissionData.not_submitted_count}</span>
              </div>
            )}
          </div>

          <div className="card-body">
            {loadingSubmissions ? (
              <PortalLoading label="Loading submissions..." />
            ) : !submissionData ? (
              <EmptyState title="Select assigned work to view submissions" />
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead className="table-light">
                    <tr><th>Student</th><th>Status</th><th>Submitted File</th><th>Feedback / Action</th></tr>
                  </thead>
                  <tbody>
                    {submissionData.students.map((row) => (
                      <tr key={row.student}>
                        <td>
                          <strong>{row.student_name}</strong>
                          <div className="small text-muted">{row.student_number}</div>
                        </td>
                        <td>
                          <StatusBadge value={row.status} />
                          {row.overdue && <div><span className="badge text-bg-danger mt-1">OVERDUE</span></div>}
                          {row.submission?.is_late && <div><span className="badge text-bg-warning mt-1">LATE</span></div>}
                        </td>
                        <td>
                          {row.submission ? (
                            <>
                              <div className="small mb-1">{niceDateTime(row.submission.submitted_at)}</div>
                              <a href={row.submission.submission_file_url} target="_blank" rel="noreferrer">
                                Open submission
                              </a>
                              {row.submission.student_comment && (
                                <div className="small text-muted mt-1">{row.submission.student_comment}</div>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">No file submitted</span>
                          )}
                        </td>
                        <td style={{ minWidth: 330 }}>
                          {row.submission ? (
                            <>
                              <textarea
                                rows="2"
                                className="form-control form-control-sm mb-2"
                                placeholder="Feedback to student"
                                value={
                                  feedback[row.submission.id] ??
                                  row.submission.teacher_feedback ??
                                  ""
                                }
                                onChange={(e) =>
                                  setFeedback((current) => ({
                                    ...current,
                                    [row.submission.id]: e.target.value,
                                  }))
                                }
                              />
                              <input
                                type="file"
                                className="form-control form-control-sm mb-2"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp"
                                onChange={(e) =>
                                  setFeedbackFiles((current) => ({
                                    ...current,
                                    [row.submission.id]: e.target.files?.[0] || null,
                                  }))
                                }
                              />
                              <div className="d-flex flex-wrap gap-2">
                                <button
                                  className="btn btn-sm btn-success"
                                  disabled={reviewingId === row.submission.id}
                                  onClick={() => review(row.submission, "REVIEWED")}
                                >
                                  Mark Reviewed
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  disabled={reviewingId === row.submission.id}
                                  onClick={() => review(row.submission, "RESUBMIT_REQUESTED")}
                                >
                                  Request Resubmission
                                </button>
                              </div>
                            </>
                          ) : (
                            <span className="text-muted">Awaiting student</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <h6 className="fw-bold mb-0">Other Learning Resources</h6>
        </div>
        <div className="card-body">
          {otherResources.length === 0 ? (
            <EmptyState title="No other learning resources yet" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr><th>Title</th><th>Class</th><th>Subject</th><th>Type</th><th>Due / Publish</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {otherResources.map((item) => (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>{item.class_group_name}</td>
                      <td>{item.subject_name}</td>
                      <td><StatusBadge value={item.resource_type} /></td>
                      <td>{niceDate(item.due_date || item.publish_date)}</td>
                      <td><StatusBadge value={item.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeacherLearningPage;
